import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import { X, Check, Search } from 'lucide-react'
import { KANBAN_COLUNAS } from '../data/kanban'

const STATUS_STYLE = {
  A_FABRICAR: '#94A3B8', FABRICANDO: '#FBBF24', FABRICADO: '#60A5FA',
  EXPEDICAO:  '#4ADE80', ROTA: '#FB923C',        POS_VENDA: '#C084FC',
}

const CONF_LABELS = { '': '—', 'Marcado': 'Marcado', 'Confirmado': '✓ Confirmado' }

function SectionHeader({ label }) {
  return (
    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 px-4 pt-4 pb-1">{label}</p>
  )
}

function CheckRow({ checked, label, onClick, colorDot, colorBg }) {
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-1.5 hover:bg-slate-50 cursor-pointer text-sm text-slate-700"
    >
      {checked ? (
        <span
          className="w-4 h-4 rounded-sm shrink-0 flex items-center justify-center"
          style={{ backgroundColor: '#F5C200' }}
        >
          <Check size={10} strokeWidth={3} className="text-[#111111]" />
        </span>
      ) : (
        <span className="w-4 h-4 rounded-sm shrink-0 border border-slate-300" />
      )}
      {colorDot && (
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: colorDot }} />
      )}
      {colorBg && (
        <span className="w-2.5 h-2.5 rounded-full border shrink-0" style={{ backgroundColor: colorBg }} />
      )}
      <span className="flex-1">{label}</span>
    </div>
  )
}

export function FiltroDrawer({
  open,
  onClose,
  filtros,
  toggle,
  set,
  limpar,
  totalAtivos,
  pedidos = [],
  sections = {},
  allTags = [],
}) {
  // Hooks SEMPRE antes de qualquer return. Quando o drawer está fechado
  // ainda computamos as listas (é barato) e retornamos null abaixo.
  // NÃO mover este return pra cima das chamadas a useMemo — viola Rules
  // of Hooks e causa React error #310.
  const vendedoras = useMemo(
    () => [...new Set(pedidos.map(p => p.vendedora).filter(Boolean))].sort(),
    [pedidos]
  )

  const cidades = useMemo(
    () => [...new Set(pedidos.map(p => p.cliente?.cidade).filter(Boolean))].sort(),
    [pedidos]
  )

  const horarios = useMemo(
    () => [...new Set(pedidos.map(p => p.horarioEntrega).filter(Boolean))].sort(),
    [pedidos]
  )

  if (!open) return null

  return createPortal(
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Drawer panel */}
      <div
        style={{ position: 'fixed', right: 0, top: 64, width: 300, height: 'calc(100vh - 64px)', zIndex: 50 }}
        className="bg-white border-l border-slate-200 shadow-2xl flex flex-col"
        onMouseDown={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#111111] shrink-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-white">Filtros</p>
            {totalAtivos > 0 && (
              <span className="bg-[#F5C200] text-[#111111] text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {totalAtivos}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {totalAtivos > 0 && (
              <button
                onClick={limpar}
                className="text-[11px] text-slate-400 hover:text-red-400 flex items-center gap-1 transition-colors"
              >
                <X size={10} /> Limpar tudo
              </button>
            )}
            <button onClick={onClose} className="p-0.5 rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1">

          {/* Busca */}
          <SectionHeader label="Busca" />
          <div className="px-4 pb-3">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                value={filtros.busca}
                onChange={e => set('busca', e.target.value)}
                placeholder="Nome, nº, cidade..."
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-[#F5C200]"
              />
            </div>
          </div>

          {/* Status */}
          <div className="border-t border-slate-100">
            <SectionHeader label="Status" />
            {KANBAN_COLUNAS.map(col => (
              <CheckRow
                key={col.id}
                checked={filtros.status.includes(col.id)}
                label={col.label}
                colorDot={STATUS_STYLE[col.id]}
                onClick={() => toggle('status', col.id)}
              />
            ))}
          </div>

          {/* Origem */}
          <div className="border-t border-slate-100">
            <SectionHeader label="Origem" />
            <CheckRow
              checked={filtros.origem.includes('local')}
              label="Mini Deck"
              onClick={() => toggle('origem', 'local')}
            />
            <CheckRow
              checked={filtros.origem.includes('bling')}
              label="Bling"
              onClick={() => toggle('origem', 'bling')}
            />
          </div>

          {/* Prazo */}
          <div className="border-t border-slate-100">
            <SectionHeader label="Prazo" />
            <div className="px-4 pb-3 flex items-center gap-2">
              <input
                type="date"
                value={filtros.prazoInicio}
                onChange={e => set('prazoInicio', e.target.value)}
                className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-[#F5C200]"
              />
              <span className="text-[10px] text-slate-400 shrink-0">até</span>
              <input
                type="date"
                value={filtros.prazoFim}
                onChange={e => set('prazoFim', e.target.value)}
                className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-[#F5C200]"
              />
            </div>
          </div>

          {/* Valor */}
          <div className="border-t border-slate-100">
            <SectionHeader label="Valor (R$)" />
            <div className="px-4 pb-3 flex items-center gap-2">
              <input
                type="number"
                value={filtros.valorMin}
                onChange={e => set('valorMin', e.target.value)}
                placeholder="Mín"
                className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-[#F5C200]"
              />
              <span className="text-[10px] text-slate-400 shrink-0">até</span>
              <input
                type="number"
                value={filtros.valorMax}
                onChange={e => set('valorMax', e.target.value)}
                placeholder="Máx"
                className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-[#F5C200]"
              />
            </div>
          </div>

          {/* Vendedora */}
          {vendedoras.length > 0 && (
            <div className="border-t border-slate-100">
              <SectionHeader label="Vendedora" />
              {vendedoras.map(v => (
                <CheckRow
                  key={v}
                  checked={filtros.vendedora.includes(v)}
                  label={v}
                  onClick={() => toggle('vendedora', v)}
                />
              ))}
            </div>
          )}

          {/* Cidade */}
          {cidades.length > 0 && (
            <div className="border-t border-slate-100">
              <SectionHeader label="Cidade" />
              {cidades.map(c => (
                <CheckRow
                  key={c}
                  checked={filtros.cidade.includes(c)}
                  label={c}
                  onClick={() => toggle('cidade', c)}
                />
              ))}
            </div>
          )}

          {/* Tags (opcional) */}
          {sections.tags && allTags.length > 0 && (
            <div className="border-t border-slate-100">
              <SectionHeader label="Etiquetas" />
              {allTags.map(tag => (
                <CheckRow
                  key={tag.id}
                  checked={filtros.tags.includes(tag.id)}
                  label={tag.nome}
                  colorDot={tag.cor}
                  onClick={() => toggle('tags', tag.id)}
                />
              ))}
            </div>
          )}

          {/* Confirmação (opcional) */}
          {sections.confirmacao && (
            <div className="border-t border-slate-100">
              <SectionHeader label="Confirmação" />
              {Object.entries(CONF_LABELS).map(([value, label]) => (
                <CheckRow
                  key={value}
                  checked={filtros.confirmacao.includes(value)}
                  label={label}
                  onClick={() => toggle('confirmacao', value)}
                />
              ))}
            </div>
          )}

          {/* Horário (opcional) */}
          {sections.horario && horarios.length > 0 && (
            <div className="border-t border-slate-100">
              <SectionHeader label="Horário de entrega" />
              {horarios.map(h => (
                <CheckRow
                  key={h}
                  checked={filtros.horario.includes(h)}
                  label={h}
                  onClick={() => toggle('horario', h)}
                />
              ))}
            </div>
          )}

          <div className="h-4" />
        </div>
      </div>
    </>,
    document.body
  )
}
